/**
 * Fire Calculator — shared Interactivity API store.
 *
 * Manages filter state (risikoklasse, etasjer) and derived results
 * for the fire-filters and fire-results blocks.
 */

import { store, getContext } from "@wordpress/interactivity";

type Status = "approved" | "conditional" | "not-applicable";

interface LookupRow {
	virksomhet: string;
	antall_etasjer: string;
	risikoklasse: string;
	brannklasse: string;
	branncelle_under_200m2_kledning: string;
	branncelle_over_200m2_kledning: string;
	romningsvei_kledning: string;
	sjakter_hulrom_kledning: string;
	product_value: string;
	statuses: Record<string, Status>;
}

interface FilterOptions {
	risikoklasse: string[];
	antall_etasjer: string[];
}

interface RequirementResult {
	key: string;
	label: string;
	value: string;
	status: Status;
	statusLabel: string;
	isApproved: boolean;
	isConditional: boolean;
	isNotApplicable: boolean;
}

const REQUIREMENT_KEYS = [
	"branncelle_under_200m2_kledning",
	"branncelle_over_200m2_kledning",
	"romningsvei_kledning",
	"sjakter_hulrom_kledning",
] as const;

const STATUS_LABELS: Record<Status, string> = {
	approved: "Kan benyttes",
	conditional: "Avklares med RIB",
	"not-applicable": "Ikke aktuelt",
};

interface FireState {
	lookupTable: LookupRow[];
	filterOptions: FilterOptions;
	requirementLabels: Record<string, string>;
	risikoklasseVirksomhetMap: Record<string, string>;
	selectedRisikoklasse: string;
	selectedEtasjer: string;

	// Derived.
	hasRisikoklasse: boolean;
	selectedVirksomhetLabel: string;
	selectedRisikoklasseLabel: string;
	selectedEtasjerLabel: string;
	etasjerOptions: string[];
	hasResults: boolean;
	matchedRow: LookupRow | null;
	risikoklasse: string;
	brannklasse: string;
	allNotApplicable: boolean;
	showRequirementsGrid: boolean;
	showNoRequirements: boolean;
	requirements: RequirementResult[];
}

const { state } = store<{ state: FireState }>("byggma-brann-og-lyd/fire", {
	state: {
		get hasRisikoklasse(): boolean {
			return state.selectedRisikoklasse !== "";
		},

		get selectedVirksomhetLabel(): string {
			if (!state.selectedRisikoklasse) return "\u2014";
			return state.risikoklasseVirksomhetMap[state.selectedRisikoklasse] || "\u2014";
		},

		get selectedRisikoklasseLabel(): string {
			return state.selectedRisikoklasse || "\u2014";
		},

		get selectedEtasjerLabel(): string {
			return state.selectedEtasjer || "\u2014";
		},

		get etasjerOptions(): string[] {
			if (!state.selectedRisikoklasse) {
				return state.filterOptions.antall_etasjer ?? [];
			}

			const matching = state.lookupTable.filter(
				(row) => row.risikoklasse === state.selectedRisikoklasse,
			);

			return [...new Set(matching.map((r) => r.antall_etasjer).filter(Boolean))];
		},

		get matchedRow(): LookupRow | null {
			if (!state.selectedRisikoklasse || !state.selectedEtasjer) return null;

			return (
				state.lookupTable.find(
					(row) =>
						row.risikoklasse === state.selectedRisikoklasse &&
						row.antall_etasjer === state.selectedEtasjer,
				) ?? null
			);
		},

		get hasResults(): boolean {
			return state.matchedRow !== null;
		},

		get risikoklasse(): string {
			return state.selectedRisikoklasse || "\u2014";
		},

		get brannklasse(): string {
			return state.matchedRow?.brannklasse || "\u2014";
		},

		get allNotApplicable(): boolean {
			const row = state.matchedRow;
			if (!row) return false;

			return REQUIREMENT_KEYS.every((key) => {
				const val = (row[key] || "").trim();
				return !val || val === "-";
			});
		},

		get showRequirementsGrid(): boolean {
			return state.hasResults && !state.allNotApplicable;
		},

		get showNoRequirements(): boolean {
			return state.hasResults && state.allNotApplicable;
		},

		get requirements(): RequirementResult[] {
			const row = state.matchedRow;
			if (!row) return [];

			return REQUIREMENT_KEYS.map((key) => {
				const status = (row.statuses?.[key] as Status) || "not-applicable";
				return {
					key,
					label: state.requirementLabels[key] || key,
					value: row[key] || "-",
					status,
					statusLabel: STATUS_LABELS[status],
					isApproved: status === "approved",
					isConditional: status === "conditional",
					isNotApplicable: status === "not-applicable",
				};
			});
		},
	},

	actions: {
		setRisikoklasse(event: Event) {
			const target = event.target as HTMLInputElement;
			state.selectedRisikoklasse = target.value;
		},

		setEtasjer(event: Event) {
			const target = event.target as HTMLInputElement;
			state.selectedEtasjer = target.value;
		},
	},
});
